<template>
  <div>
    <!-- Table Controls -->
    <div class="row mb-3 align-items-center">
      <div class="col-sm-12 col-md-6">
        <label class="d-inline-flex align-items-center mb-0">
          Show
          <b-form-select class="form-select form-select-sm mx-2" v-model="perPage" size="sm"
            :options="[10, 25, 50, 100]"></b-form-select>
          entries
        </label>
      </div>
      <div class="col-sm-12 col-md-6 text-md-end mt-3 mt-md-0">
        <b-form-input v-model="filter" type="search" placeholder="Search closed loans..."
          class="form-control form-control-sm d-inline-block w-auto"></b-form-input>
      </div>
    </div>

    <!-- Data Table -->
    <div class="table-responsive">
      <b-table :items="Items" :fields="Fields" responsive="sm" :per-page="perPage" :current-page="currentPage"
        :filter="filter" :busy="isLoading" show-empty class="align-middle table-nowrap table-hover">

        <template #table-busy>
          <div class="text-center text-primary my-5">
            <b-spinner class="align-middle" type="grow"></b-spinner>
            <div class="mt-2 fw-medium">Loading Closed Loans...</div>
          </div>
        </template>

        <template #empty>
          <div class="text-center py-5">
            <div class="mb-3">
              <i class="mdi mdi-check-decagram text-muted" style="font-size: 64px;"></i>
            </div>
            <h5 class="text-dark">No Closed Loans</h5>
            <p class="text-muted mb-4">There are currently no closed loan records available.</p>
          </div>
        </template>

        <template #cell(index)="data">{{ data.index + 1 }}</template>

        <template #cell(loan_ref)="data">
          <div class="loan-ref-link" @click="openLoanDetails(data.item.id)">
            <span class="fw-bold d-block align-items-center">
              {{ data.item.loan_number }}
              <i v-if="data.item.has_been_rescheduled" class="mdi mdi-calendar-sync text-info ms-1"
                title="Loan Expiry date was rescheduled"></i>
            </span>
            <small class="text-primary fw-bold">{{ data.item.member_name }} ({{ data.item.member_id }})</small>
          </div>
        </template>

        <template #cell(product_details)="data">
          <span class="fw-medium d-block">{{ data.item.loan_product }}</span>
          <small class="fw-bold text-muted">Rate: <span class="text-primary">{{ data.item.rate }}% {{
            data.item.rate_type ===
              'MONTHLY' ? 'Monthly' : 'Annually' }}</span></small>
        </template>

        <!-- NEW: Loan Value Column -->
        <template #cell(loan_value)="data">
          <div class="d-flex justify-content-start mb-1" style="min-width: 140px;">
            <span class="text-muted small me-2">Principal:</span>
            <span class="fw-bold text-dark">{{ userData.currency }} {{ commas(data.item.amount) }}</span>
          </div>
          <div class="d-flex justify-content-start">
            <span class="text-muted small me-2">Interest:</span>
            <span class="text-primary fw-bold">{{ userData.currency }} {{ commas(data.item.interest.toFixed(0))
            }}</span>
          </div>
        </template>

        <!-- NEW: Repayment Column -->
        <template #cell(repayment)="data">
          <div class="d-flex justify-content-start mb-1" style="min-width: 140px;">
            <span class="text-muted small me-2">Fines:</span>
            <span :class="data.item.fine_amount > 0 ? 'text-danger fw-bold' : 'text-muted'">{{ userData.currency }} {{
              commas(data.item.fine_amount) }}</span>
          </div>
          <div class="d-flex justify-content-start pt-1 border-top border-light">
            <span class="text-muted small me-2">Total Paid:</span>
            <span class="text-success fw-bold">{{ userData.currency }} {{ commas(data.item.total_paid) }}</span>
          </div>
        </template>

        <template #cell(dates)="data">
          <small class="d-block text-muted">Disbursed: <span class="text-dark fw-bold">{{
            formatDate(data.item.disbursement_date) }}</span></small>
          <small class="d-block text-muted mt-1">Closed On: <span class="text-success fw-bold">{{
            formatDate(data.item.closed_date) }}</span></small>
        </template>

        <template #cell(status)="data">
          <b-badge variant="success" class="p-2 fs-6"><i class="mdi mdi-check-all me-1"></i>{{ data.item.status
          }}</b-badge>
        </template>

        <template #cell(actions)="data">
          <b-button title="Review Loan Details" size="sm" variant="info" class="me-1 mb-1"
            @click="openLoanDetails(data.item.id)">
            <i class="mdi mdi-text-box-search-outline"></i> Review
          </b-button>
        </template>
      </b-table>
    </div>

    <!-- Pagination -->
    <div class="d-flex justify-content-end mt-3" v-if="List > 0">
      <b-pagination v-model="currentPage" :total-rows="List" :per-page="perPage"></b-pagination>
    </div>

    <!-- Loan Details Modal -->
    <b-modal id="closed-loan-details-modal" size="xl" hide-footer hide-header @hidden="resetDetailsModal">
      <!-- Custom Header -->
      <div class="modal-header border-0 pb-0 mb-4 d-flex justify-content-between">
        <div class="d-flex align-items-center">
          <div class="bg-success bg-opacity-10 p-2 me-3 d-flex align-items-center justify-content-center"
            style="border-radius: 20rem; width: 3.5rem; height: 3.5rem;">
            <i class="mdi mdi-check-decagram text-success" style="font-size: 2.2rem;"></i>
          </div>
          <div>
            <h5 class="modal-title fw-bold mb-0">
              Closed Loan Details
              <span v-if="selectedLoanDetails" class="text-muted fw-normal ms-1">#{{
                selectedLoanDetails.loan.loan_number
              }}</span>
            </h5>
            <small class="text-muted">Historic records and settled accounts</small>
          </div>
        </div>
        <button type="button" class="btn-close" @click="$bvModal.hide('closed-loan-details-modal')"></button>
      </div>

      <!-- Loading State -->
      <div v-if="isLoadingDetails" class="text-center py-5">
        <b-spinner variant="success"></b-spinner>
        <p class="mt-3 text-muted">Retrieving historic loan records...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="detailsError" class="text-center py-5">
        <i class="mdi mdi-alert-circle-outline display-4 text-danger mb-2"></i>
        <h6 class="text-danger">Failed to load loan details.</h6>
        <b-button size="sm" variant="outline-danger" class="mt-2"
          @click="fetchLoanDetails(activeDetailsLoanId)">Retry</b-button>
      </div>

      <!-- Content -->
      <div v-else-if="selectedLoanDetails" class="px-2 px-md-4">
        <!-- Tabs Section -->
        <b-tabs content-class="mt-4" nav-class="nav-tabs-custom">
          <!-- Tab 1: Loan Details -->
          <b-tab title="Loan Details" active>
            <div class="row">
              <div class="col-lg-6 pe-lg-4 mb-4 mb-lg-0 border-end-lg">
                <h6 class="text-uppercase text-muted fw-bold small mb-2"><i class="mdi mdi-account-circle me-1"></i>
                  Borrower Profile</h6>
                <div class="d-flex align-items-center mb-3 p-2 bg-light rounded border shadow-sm">
                  <div class="avatar-md me-3 flex-shrink-0">
                    <span class="avatar-title rounded-circle bg-soft-success text-white fs-2">
                      {{ selectedLoanDetails.loan.member_name.charAt(0) }}
                    </span>
                  </div>
                  <div>
                    <h5 class="mb-1 text-dark fw-bold">{{ selectedLoanDetails.loan.member_name }}</h5>
                    <p class="mb-0 text-muted small">Member ID: <strong>{{ selectedLoanDetails.loan.member_id
                    }}</strong>
                    </p>
                  </div>
                </div>

                <div class="card border-success border-opacity-50 shadow-sm mb-0">
                  <div class="card-body bg-soft-success bg-opacity-10 rounded p-4">
                    <p class="text-uppercase fw-bold text-success small mb-1"><i class="mdi mdi-scale-balance me-1"></i>
                      Final Total Paid</p>
                    <h1 class="text-success fw-bold mb-2 display-6">{{ userData.currency }} {{
                      commas(selectedLoanDetails.loan.total_paid) }}</h1>

                    <p class="text-muted small mb-4">
                      Original Principal: <strong>{{ userData.currency }} {{ commas(selectedLoanDetails.loan.amount)
                      }}</strong><br>
                      Recorded Fines: <strong class="text-danger">{{ userData.currency }} {{
                        commas(selectedLoanDetails.loan.fine_amount) }}</strong>
                    </p>
                    <h5 class="text-dark fw-bold mb-3">{{ selectedLoanDetails.loan.loan_product }}</h5>
                    <div
                      class="d-flex justify-content-between border-bottom border-success border-opacity-25 pb-2 mb-3">
                      <span class="text-muted">Interest Profile:</span>
                      <span class="fw-bold text-dark">{{ selectedLoanDetails.loan.rate }}% {{
                        selectedLoanDetails.loan.rate_type }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-lg-6 ps-lg-4">
                <h6 class="text-uppercase text-muted fw-bold small mb-2"><i class="mdi mdi-clock-outline me-1"></i>
                  Historic Timeline</h6>
                <ul class="list-group list-group-flush mb-4 border rounded shadow-sm">
                  <!-- ADDED: Application Date -->
                  <li class="list-group-item d-flex justify-content-between align-items-center py-3">
                    <span class="text-muted">Applied On</span>
                    <span class="fw-bold text-dark">{{ formatDate(selectedLoanDetails.loan.application_date) }}</span>
                  </li>

                  <!-- ADDED: Approved Date -->
                  <li class="list-group-item d-flex justify-content-between align-items-center py-3">
                    <span class="text-muted">Approved On</span>
                    <span class="fw-bold text-dark">{{ formatDate(selectedLoanDetails.loan.approved_date) }}</span>
                  </li>

                  <li class="list-group-item d-flex justify-content-between align-items-center py-3">
                    <span class="text-muted">Disbursed On</span>
                    <span class="fw-bold text-dark">{{ formatDate(selectedLoanDetails.loan.disbursement_date) }}</span>
                  </li>

                  <!-- UPDATED: Expiry Date with clear Reschedule styling -->
                  <li class="list-group-item d-flex justify-content-between align-items-center py-3">
                    <span class="text-muted">Expected Expiry</span>
                    <div class="text-end">
                      <span class="fw-bold text-dark"
                        :class="{ 'text-decoration-line-through text-muted fw-normal': selectedLoanDetails.loan.has_been_rescheduled }">
                        {{ formatDate(selectedLoanDetails.loan.expiry_date) }}
                      </span>
                      <div v-if="selectedLoanDetails.loan.has_been_rescheduled" class="text-info small mt-1 fw-bold">
                        <i class="mdi mdi-calendar-sync"></i> Rescheduled to {{
                          formatDate(selectedLoanDetails.loan.latest_reschedule_date) }}
                      </div>
                    </div>
                  </li>

                  <li class="list-group-item d-flex justify-content-between align-items-center py-3 bg-soft-success">
                    <span class="text-success fw-medium">Successfully Closed</span>
                    <span class="badge bg-success p-2 fs-6"><i class="mdi mdi-check-decagram me-1"></i> {{
                      formatDate(selectedLoanDetails.loan.closed_date) }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </b-tab>

          <!-- Tab 2: Guarantors -->
          <b-tab title="Guarantors">
            <div class="row">
              <div class="col-md-6 mb-3">
                <h6 class="text-primary border-bottom pb-2 mb-3"><i class="mdi mdi-account-group me-1"></i> Member
                  Guarantors</h6>
                <div v-if="selectedLoanDetails.guarantors.length > 0">
                  <div v-for="g in selectedLoanDetails.guarantors" :key="'g' + g.id"
                    class="d-flex align-items-center p-2 mb-2 border rounded bg-white shadow-sm">
                    <div class="avatar-sm me-3 flex-shrink-0">
                      <span class="avatar-title rounded-circle bg-soft-success text-white fs-5"><i
                          class="mdi mdi-account-check"></i></span>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="mb-0 text-dark font-size-15">{{ g.name || g.member_id }}</h6>
                      <small class="text-muted">Member ID: {{ g.member_id }}</small>
                    </div>
                  </div>
                </div>
                <div class="text-muted p-4 text-center border border-dashed rounded bg-light mb-4" v-else>
                  <i class="mdi mdi-account-off-outline fs-3 d-block mb-1"></i>
                  <small>No member guarantors were attached.</small>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <h6 class="text-primary border-bottom pb-2 mb-3"><i class="mdi mdi-account-group-outline me-1"></i>
                  Guest
                  Guarantors</h6>
                <div v-if="selectedLoanDetails.guest_guarantors.length > 0">
                  <div v-for="gg in selectedLoanDetails.guest_guarantors" :key="'gg' + gg.id"
                    class="d-flex align-items-center p-2 mb-2 border rounded bg-white shadow-sm">
                    <div class="avatar-sm me-3 flex-shrink-0">
                      <span class="avatar-title rounded-circle bg-soft-secondary text-white fs-5"><i
                          class="mdi mdi-account-outline"></i></span>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="mb-0 text-dark font-size-15">{{ gg.name }}</h6>
                      <small class="text-muted"><i class="mdi mdi-phone me-1"></i>{{ gg.contact }}</small>
                    </div>
                  </div>
                </div>
                <div class="text-muted p-4 text-center border border-dashed rounded bg-light mb-4" v-else>
                  <i class="mdi mdi-account-off-outline fs-3 d-block mb-1"></i>
                  <small>No guest guarantors provided.</small>
                </div>
              </div>
            </div>
          </b-tab>

          <!-- Tab 3: Collateral -->
          <b-tab title="Collateral">
            <div class="row" v-if="selectedLoanDetails.collaterals.length > 0">
              <div class="col-md-6 mb-3" v-for="c in selectedLoanDetails.collaterals" :key="'c' + c.id">
                <div class="p-3 border rounded bg-white shadow-sm h-100">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <h6 class="mb-0 text-dark font-size-15 fw-bold">{{ c.name }}</h6>
                    <span v-if="c.value" class="badge bg-soft-success text-success fs-6">{{ userData.currency }} {{
                      commas(c.value) }}</span>
                  </div>
                  <p class="small text-muted mb-2" v-if="c.description">{{ c.description }}</p>
                  <div v-if="c.media_url" class="mt-3 pt-2 border-top border-light">
                    <a :href="c.media_url" target="_blank"
                      class="btn btn-sm btn-soft-info d-inline-flex align-items-center">
                      <i class="mdi mdi-file-document-outline fs-5 me-1"></i> View Attached Document
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-muted p-4 text-center border border-dashed rounded bg-light mb-4">
              <i class="mdi mdi-shield-off-outline fs-3 d-block mb-1"></i>
              <small>No collateral security pledged.</small>
            </div>
          </b-tab>

          <!-- Tab 4: Loan Reschedules -->
          <b-tab title="Loan Reschedules">
            <div v-if="selectedLoanDetails.reschedules.length > 0" class="table-responsive border rounded shadow-sm">
              <table class="table table-bordered table-sm align-middle mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Requested On</th>
                    <th>Shifted From</th>
                    <th>Shifted To</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="res in selectedLoanDetails.reschedules" :key="res.id">
                    <td>{{ formatDate(res.created_at) }}</td>
                    <td class="text-danger fw-medium"><del>{{ formatDate(res.from_date) }}</del></td>
                    <td class="text-success fw-bold">{{ formatDate(res.to_date) }}</td>
                    <td>{{ res.reason || 'N/A' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-muted p-4 text-center border border-dashed rounded bg-light mb-4">
              <i class="mdi mdi-calendar-check fs-3 d-block mb-1"></i>
              <small>This loan was settled on its original timeline.</small>
            </div>
          </b-tab>

          <!-- Tab 5: Loan Repayments -->
          <b-tab title="Loan Repayments">
            <div v-if="selectedLoanDetails.repayments.length > 0" class="table-responsive border rounded shadow-sm">
              <table class="table table-bordered table-sm align-middle mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Date Paid</th>
                    <th>Amount</th>
                    <th>Reference</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="rep in selectedLoanDetails.repayments" :key="rep.id">
                    <td>{{ formatDate(rep.date_of_payment) }}</td>
                    <td class="fw-bold text-success">
                      {{ userData.currency }} {{ commas(rep.amount_paid) }}
                      <small class="d-block text-muted">{{ rep.mode_of_payment }}</small>
                    </td>
                    <td>{{ rep.reference || '-' }}</td>
                    <td>
                      <b-badge
                        :variant="rep.status === 'APPROVED' ? 'success' : (rep.status === 'FAILED' ? 'danger' : 'warning')">
                        {{ rep.status }}
                      </b-badge>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-muted p-4 text-center border border-dashed rounded bg-light mb-4">
              <i class="mdi mdi-cash-remove fs-3 d-block mb-1"></i>
              <small>No repayments have been recorded for this loan.</small>
            </div>
          </b-tab>
        </b-tabs>
      </div>

      <!-- Action Footer -->
      <div class="d-flex justify-content-end mt-3 mb-2 pt-3 border-top bg-light rounded-bottom px-3 py-3 mx-n3 mb-n3">
        <b-button variant="light" @click="$bvModal.hide('closed-loan-details-modal')"
          class="px-4 shadow-sm border">Close</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import { closedloanComputed, closedloanMethods } from "@/state/helpers";

export default {
  directives: {
    numberFormat: {
      bind(el) {
        function handler() {
          if (el._formatting) return;
          const digits = el.value.replace(/\D/g, '');
          const formatted = digits ? parseInt(digits, 10).toLocaleString('en-US') : '';
          if (el.value !== formatted) {
            el._formatting = true;
            el.value = formatted;
            el.dispatchEvent(new Event('input', { bubbles: true }));
            el._formatting = false;
          }
        }
        el.addEventListener('input', handler);
        el._numberFormatHandler = handler;
      },
      unbind(el) { el.removeEventListener('input', el._numberFormatHandler); }
    }
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("user")) || { currency: 'UGX' },
      perPage: 10,
      currentPage: 1,
      filter: null,

      activeDetailsLoanId: null,
      selectedLoanDetails: null,
      isLoadingDetails: false,
      detailsError: false,

      fields: [
        { key: 'index', label: '#', class: 'align-middle', visible: true },
        { key: 'loan_ref', label: 'Loan Ref', sortable: true, class: 'align-middle', visible: true },
        { key: 'product_details', label: 'Product Info', class: 'align-middle', visible: true },

        // Split the visuals across two columns
        { key: 'loan_value', label: 'Loan Value', class: 'align-middle', visible: true },
        { key: 'repayment', label: 'Repayment', class: 'align-middle', visible: true },

        { key: 'dates', label: 'Timeline', class: 'align-middle', visible: true },
        { key: 'actions', label: 'Actions', class: 'align-middle text-end', visible: true },
        { key: 'id', label: 'ID', class: 'align-middle', visible: false }
      ]
    };
  },
  computed: {
    ...closedloanComputed,
    IsBusy() { return this.isLoading; },
    List() { return this.closedloans ? this.closedloans.length : 0; },
    Fields() { return this.fields.filter(field => field.visible); },
    Items() { return this.closedloans ? this.closedloans.map((d) => ({ ...d })) : []; },
  },
  methods: {
    ...closedloanMethods,
    commas(x) {
      return x ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : 0;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date)) return dateString;
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    },

    resetDetailsModal() {
      this.selectedLoanDetails = null;
      this.detailsError = false;
      this.activeDetailsLoanId = null;
    },

    openLoanDetails(loanId) {
      this.activeDetailsLoanId = loanId;
      this.selectedLoanDetails = null;
      this.$bvModal.show('closed-loan-details-modal');
      this.fetchLoanDetails(loanId);
    },

    async fetchLoanDetails(loanId) {
      this.isLoadingDetails = true;
      this.detailsError = false;

      try {
        const response = await axios.get(`/loans/closed/${loanId}/details`);
        this.selectedLoanDetails = response.data;
      } catch (error) {
        this.detailsError = true;
        this.$toast.error('Failed to load loan details.');
      } finally {
        this.isLoadingDetails = false;
      }
    }
  }
};
</script>

<style scoped>
@media (min-width: 992px) {
  .border-end-lg {
    border-right: 1px solid #dee2e6;
  }
}

.border-dashed {
  border-style: dashed !important;
  border-width: 2px !important;
  border-color: #dee2e6 !important;
}

.loan-ref-link {
  text-decoration: none;
  color: inherit;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
}

.loan-ref-link:hover {
  color: var(--bs-success);
  text-decoration: underline;
  display: inline-block;
}

/* Custom styling for tabs to make them look cleaner inside modal */
:deep(.nav-tabs-custom .nav-link) {
  color: #6c757d;
  font-weight: 500;
  border: none;
  border-bottom: 2px solid transparent;
}

:deep(.nav-tabs-custom .nav-link.active) {
  color: var(--bs-success);
  border-bottom: 2px solid var(--bs-success);
  background-color: transparent;
}
</style>